import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "../../assets/css/dashboard.css";
import { getAllOccasions } from "../../utils/getAllOccasions";
import { getLookupUtils } from "../../utils/lookup";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import axios from "axios";
import { canAccessModule } from "../../utils/eventAccessModules";
import { canEditPage } from "../../utils/pagePermissions";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Sector,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  statsForAllocatedOccasion,
  statsForinvitedOccasion,
  confirmedGuestStats,
  confirmedOccasionGuestStats,
  getAccommodationStatsByOccasion,
} from "../../server/Api";
import { getOccasionTheme } from "../../utils/occasionThemes";

const smoothTooltipProps = {
  allowEscapeViewBox: { x: false, y: false },
  offset: 10,
  animationDuration: 480,
  animationEasing: "ease-out",
  isAnimationActive: true,
  wrapperStyle: {
    zIndex: 20,
    pointerEvents: "none",
    outline: "none",
    transition: "opacity 0.35s ease, transform 0.35s ease",
  },
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  // Pie: single slice. Bar: may include stacked series.
  if (payload.length === 1 && payload[0].name != null && label == null) {
    const item = payload[0];
    return (
      <div className="dash-chart-tooltip">
        <span>{item.name ?? item.payload?.name}</span>
        <strong>{item.value}</strong>
      </div>
    );
  }

  return (
    <div className="dash-chart-tooltip dash-chart-tooltip-stack">
      {label ? <div className="dash-chart-tooltip-label">{label}</div> : null}
      {payload.map((item) => (
        <div key={item.dataKey || item.name} className="dash-chart-tooltip-row">
          <span>{item.name}</span>
          <strong>{item.value}</strong>
        </div>
      ))}
    </div>
  );
};

const EmptyChart = ({ label = "No data" }) => (
  <div className="dash-chart-empty">{label}</div>
);

const renderActiveShape = (props) => {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    value,
  } = props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 10}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        stroke="#fff"
        strokeWidth={2}
      />
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={outerRadius + 12}
        outerRadius={outerRadius + 16}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <text
        x={cx}
        y={cy - 6}
        textAnchor="middle"
        className="dash-pie-center-label"
      >
        {payload.name}
      </text>
      <text
        x={cx}
        y={cy + 12}
        textAnchor="middle"
        className="dash-pie-center-value"
      >
        {value}
      </text>
    </g>
  );
};

const MiniPie = ({ data, colors, showZero = true }) => {
  const palette = colors?.length ? colors : getOccasionTheme(0).chart;
  const rows = (data || []).map((d) => ({
    ...d,
    value: Number(d.value) || 0,
  }));
  const pieRows = rows.filter((d) => d.value > 0);
  const legendRows = showZero ? rows : pieRows;

  if (!legendRows.length) return <EmptyChart />;

  return (
    <div className="dash-pie-wrap">
      <div className="dash-pie-chart">
        {pieRows.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieRows}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={34}
                outerRadius={54}
                paddingAngle={2}
                stroke="#fff"
                strokeWidth={2}
                activeShape={renderActiveShape}
                isAnimationActive
                animationDuration={650}
                animationEasing="ease-out"
              >
                {pieRows.map((entry, index) => (
                  <Cell
                    key={`${entry.name}-${index}`}
                    fill={palette[index % palette.length]}
                  />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} {...smoothTooltipProps} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart label="No counts yet" />
        )}
      </div>
      <ul className="dash-pie-legend">
        {legendRows.map((entry, index) => {
          const colorIndex = pieRows.findIndex((p) => p.name === entry.name);
          const color =
            palette[(colorIndex >= 0 ? colorIndex : index) % palette.length];
          return (
            <li key={entry.name}>
              <span
                className="dash-pie-swatch"
                style={{
                  backgroundColor: entry.value > 0 ? color : "#c5d0d0",
                }}
              />
              <span className="dash-pie-name" title={entry.name}>
                {entry.name}
              </span>
              <strong className="dash-pie-count">{entry.value}</strong>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchTerm] = useState("");
  const [occasions, setOccasions] = useState([]);
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);
  const user = useSelector((state) => state.auth.user);
  const canOpenOccasionMaster = canAccessModule(
    selectedEvent,
    "occasion-master",
    user
  );
  const canCreateOccasion = canEditPage(
    selectedEvent,
    "occasion-master",
    user
  );
  const [selectedOccasionId, setSelectedOccasionId] = useState(null);
  const [foodPreferences, setFoodPreferences] = useState([]);
  const [genders, setGenders] = useState([]);
  const [stats, setStats] = useState(null);
  const [occasionStats, setOccasionStats] = useState({});
  const [invitedOccasionStats, setInvitedOccasionStats] = useState({});
  const [confirmedOccasionStats, setConfirmedOccasionStats] = useState({});

  const [showExpected, setShowExpected] = useState(false);
  const [showAllocated, setShowAllocated] = useState(false);
  const [showInvited, setShowInvited] = useState(false);
  const [showConfirmed, setShowConfirmed] = useState(true);
  const [accommodationData, setAccommodationData] = useState({
    summary: null,
    hotels: [],
  });

  const handleToggle = (key) => {
    if (key === "expected") setShowExpected((prev) => !prev);
    if (key === "allocated") setShowAllocated((prev) => !prev);
    if (key === "invited") setShowInvited((prev) => !prev);
    if (key === "confirmed") setShowConfirmed((prev) => !prev);
  };

  const activeMetricCount = useMemo(() => {
    return [showExpected, showAllocated, showInvited, showConfirmed].filter(
      Boolean
    ).length;
  }, [showExpected, showAllocated, showInvited, showConfirmed]);

  const metricsDensityClass =
    activeMetricCount <= 0
      ? "metrics-density-0"
      : `metrics-density-${Math.min(4, activeMetricCount)}`;

  const activeOccasions = useMemo(
    () =>
      occasions.filter(
        (o) =>
          o.isOccasionActive &&
          String(o.OccasionName || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      ),
    [occasions, searchTerm]
  );

  const selectedOccasion = useMemo(
    () => activeOccasions.find((o) => o._id === selectedOccasionId),
    [activeOccasions, selectedOccasionId]
  );

  const selectedOccasionIndex = useMemo(() => {
    const idx = activeOccasions.findIndex((o) => o._id === selectedOccasionId);
    return idx >= 0 ? idx : 0;
  }, [activeOccasions, selectedOccasionId]);

  const theme = useMemo(
    () => getOccasionTheme(selectedOccasionIndex),
    [selectedOccasionIndex]
  );

  const themeStyle = useMemo(
    () => ({
      "--dash-primary": theme.primary,
      "--dash-soft": theme.soft,
      "--dash-border": theme.border,
    }),
    [theme]
  );

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (!selectedEvent?.eventId) return;
        const res = await axios.get(
          statsForAllocatedOccasion(selectedEvent.eventId)
        );
        setOccasionStats(res.data.allocatedOccasionStats || {});
      } catch (err) {
        console.error("Error fetching occasion stats", err);
      }
    };
    fetchStats();
  }, [selectedEvent]);

  useEffect(() => {
    const fetchInvitedStats = async () => {
      try {
        if (!selectedEvent?.eventId) return;
        const res = await axios.get(
          statsForinvitedOccasion(selectedEvent.eventId)
        );
        setInvitedOccasionStats(res.data.invitedOccasionStats || {});
      } catch (err) {
        console.error("Error fetching invited occasion stats", err);
      }
    };
    fetchInvitedStats();
  }, [selectedEvent]);

  useEffect(() => {
    const fetchConfirmedStats = async () => {
      try {
        if (!selectedEvent?.eventId) return;
        const res = await axios.get(confirmedGuestStats(selectedEvent.eventId));
        setConfirmedOccasionStats(res.data.confirmedOccasionStats || {});
      } catch (err) {
        console.error("Error fetching confirmed occasion stats", err);
      }
    };
    fetchConfirmedStats();
  }, [selectedEvent]);

  useEffect(() => {
    const fetchOccasions = async () => {
      try {
        if (!selectedEvent?.eventId) return;
        const list = await getAllOccasions(selectedEvent.eventId);
        setOccasions(list || []);
      } catch (error) {
        toast.error("Failed to load occasions");
      }
    };
    fetchOccasions();
  }, [selectedEvent]);

  useEffect(() => {
    if (!activeOccasions.length) {
      setSelectedOccasionId(null);
      return;
    }
    const stillValid = activeOccasions.some((o) => o._id === selectedOccasionId);
    if (!stillValid) {
      setSelectedOccasionId(activeOccasions[0]._id);
    }
  }, [activeOccasions, selectedOccasionId]);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const foodRes = await getLookupUtils(8);
        if (foodRes?.data) {
          setFoodPreferences(
            foodRes.data.sort(
              (a, b) => a.Lookup_Value_Order - b.Lookup_Value_Order
            )
          );
        }
        const genderRes = await getLookupUtils(5);
        if (genderRes?.data) {
          setGenders(
            genderRes.data.sort(
              (a, b) => a.Lookup_Value_Order - b.Lookup_Value_Order
            )
          );
        }
      } catch (error) {
        console.error("Error fetching lookups:", error);
      }
    };
    fetchLookups();
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (!selectedEvent?.eventId || !selectedOccasionId) {
          setStats(null);
          return;
        }
        const res = await axios.get(
          confirmedOccasionGuestStats(selectedEvent.eventId, selectedOccasionId)
        );
        setStats(res.data.stats);
      } catch (error) {
        console.error("Error fetching confirmed occasion stats:", error);
        setStats(null);
      }
    };
    fetchStats();
  }, [selectedEvent, selectedOccasionId]);

  useEffect(() => {
    const fetchAccommodation = async () => {
      try {
        if (!selectedEvent?.eventId || !selectedOccasionId) {
          setAccommodationData({ summary: null, hotels: [] });
          return;
        }
        const res = await axios.get(
          getAccommodationStatsByOccasion(
            selectedEvent.eventId,
            selectedOccasionId
          )
        );
        setAccommodationData({
          summary: res.data?.data?.summary || null,
          hotels: res.data?.data?.hotels || [],
        });
      } catch (err) {
        console.error("Error fetching accommodation stats", err);
        setAccommodationData({ summary: null, hotels: [] });
      }
    };
    fetchAccommodation();
  }, [selectedEvent, selectedOccasionId]);

  const foodChartData = useMemo(
    () =>
      foodPreferences.map((item) => ({
        name: item.Lookup_Value_Name,
        value: Number(stats?.foodPreference?.[item.Lookup_Value_Name] || 0),
      })),
    [foodPreferences, stats]
  );

  const genderChartData = useMemo(
    () =>
      genders.map((item) => ({
        name: item.Lookup_Value_Name,
        value: Number(stats?.gender?.[item.Lookup_Value_Name] || 0),
      })),
    [genders, stats]
  );

  const demographicChartData = useMemo(
    () => [
      { name: "Adult", value: Number(stats?.demographic?.Adult || 0) },
      { name: "Children", value: Number(stats?.demographic?.Minor || 0) },
    ],
    [stats]
  );

  const requestChartData = useMemo(
    () => [
      { name: "Special Request", value: Number(stats?.specialRequest || 0) },
      { name: "Special Needs", value: Number(stats?.specialNeeds || 0) },
      { name: "Allergic", value: Number(stats?.allergic || 0) },
    ],
    [stats]
  );

  const occupancyPieData = useMemo(() => {
    const occupied = Number(accommodationData.summary?.occupied || 0);
    const available = Number(accommodationData.summary?.available || 0);
    return [
      { name: "Occupied", value: occupied },
      { name: "Available", value: available },
    ];
  }, [accommodationData]);

  const hotelBarData = useMemo(
    () =>
      (accommodationData.hotels || []).map((h) => ({
        name:
          h.hotelName?.length > 14
            ? `${h.hotelName.slice(0, 14)}…`
            : h.hotelName,
        Occupied: h.occupied,
        Available: h.available,
      })),
    [accommodationData]
  );

  return (
    <div
      className="container-fluid dashboard-page dashboard-fit"
      style={themeStyle}
    >
      <div className="dashboard-fit-scroll">
        {/* Occasion Count */}
        <div className={`occasion-count dash-panel ${metricsDensityClass}`}>
          <div className="occasion-metrics-layout">
            <div className="occasion-metric-legend" aria-label="Metric toggles">
              <div className="legend-section-title">
                <h3 className="section-title mb-0">Occasion Count</h3>
              </div>
              <div
                className={`legend-row metric-capacity${
                  showExpected ? " is-on" : ""
                }`}
              >
                <label className="legend-switch" htmlFor="expectedToggle">
                  <input
                    id="expectedToggle"
                    className="legend-toggle"
                    type="checkbox"
                    role="switch"
                    checked={showExpected}
                    onChange={() => handleToggle("expected")}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="legend-swatch" aria-hidden="true" />
                  <span className="legend-label">Capacity</span>
                </label>
              </div>
              <div
                className={`legend-row metric-allocated${
                  showAllocated ? " is-on" : ""
                }`}
              >
                <label className="legend-switch" htmlFor="allocatedToggle">
                  <input
                    id="allocatedToggle"
                    className="legend-toggle"
                    type="checkbox"
                    role="switch"
                    checked={showAllocated}
                    onChange={() => handleToggle("allocated")}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="legend-swatch" aria-hidden="true" />
                  <span className="legend-label">Allocated</span>
                </label>
              </div>
              <div
                className={`legend-row metric-invited${
                  showInvited ? " is-on" : ""
                }`}
              >
                <label className="legend-switch" htmlFor="invitedToggle">
                  <input
                    id="invitedToggle"
                    className="legend-toggle"
                    type="checkbox"
                    role="switch"
                    checked={showInvited}
                    onChange={() => handleToggle("invited")}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="legend-swatch" aria-hidden="true" />
                  <span className="legend-label">Invite Sent</span>
                </label>
              </div>
              <div
                className={`legend-row metric-confirmed${
                  showConfirmed ? " is-on" : ""
                }`}
              >
                <label className="legend-switch" htmlFor="confirmedToggle">
                  <input
                    id="confirmedToggle"
                    className="legend-toggle"
                    type="checkbox"
                    role="switch"
                    checked={showConfirmed}
                    onChange={() => handleToggle("confirmed")}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="legend-swatch" aria-hidden="true" />
                  <span className="legend-label">RSVP Confirmed</span>
                </label>
              </div>
            </div>

            <div className="occasion-cards-track">
              {!activeOccasions.length ? (
                <div className="occasion-empty occasion-empty-cta">
                  <p className="occasion-empty-text mb-0">
                    No occasions yet for this event.
                  </p>
                  {canOpenOccasionMaster && (
                    <button
                      type="button"
                      className="btn occasion-create-btn"
                      onClick={() => navigate("/occasion-master")}
                    >
                      {canCreateOccasion
                        ? "+ Create Occasion"
                        : "Go to Occasion Master"}
                    </button>
                  )}
                </div>
              ) : (
                activeOccasions.map((occasion, index) => {
                  const cardTheme = getOccasionTheme(index);
                  return (
                    <div key={occasion._id} className="occasion-card-wrap">
                      <div
                        className={`event-card highlight occard${
                          selectedOccasionId === occasion._id ? " active" : ""
                        }`}
                        style={{
                          "--card-accent": cardTheme.primary,
                          "--card-soft": cardTheme.soft,
                          "--card-border": cardTheme.border,
                        }}
                        onClick={() => setSelectedOccasionId(occasion._id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedOccasionId(occasion._id);
                          }
                        }}
                      >
                        <h5 title={occasion.OccasionName}>
                          {occasion.OccasionName}
                        </h5>
                        <div className="occard-metrics">
                          <div
                            className={`count-line metric-capacity${
                              showExpected ? " is-on" : ""
                            }`}
                          >
                            <span className="value">
                              {occasion.OccasionCapacity}
                            </span>
                          </div>
                          <div
                            className={`count-line metric-allocated${
                              showAllocated ? " is-on" : ""
                            }`}
                          >
                            <span className="value">
                              {occasionStats[occasion._id] || 0}
                            </span>
                          </div>
                          <div
                            className={`count-line metric-invited${
                              showInvited ? " is-on" : ""
                            }`}
                          >
                            <span className="value">
                              {invitedOccasionStats[occasion._id] || 0}
                            </span>
                          </div>
                          <div
                            className={`count-line metric-confirmed${
                              showConfirmed ? " is-on" : ""
                            }`}
                          >
                            <span className="value">
                              {confirmedOccasionStats[occasion._id] || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Stats charts — occasion filtered */}
        <div className="dash-charts-row">
          <div className="dash-chart-card">
            <h4 className="dash-chart-title">Food Preference</h4>
            <div className="dash-chart-body">
              <MiniPie data={foodChartData} colors={theme.chart} />
            </div>
          </div>
          <div className="dash-chart-card">
            <h4 className="dash-chart-title">Gender</h4>
            <div className="dash-chart-body">
              <MiniPie data={genderChartData} colors={theme.chart} />
            </div>
          </div>
          <div className="dash-chart-card">
            <h4 className="dash-chart-title">Demographic</h4>
            <div className="dash-chart-body">
              <MiniPie
                data={demographicChartData}
                colors={[theme.primary, theme.chart[2] || theme.chart[1]]}
              />
            </div>
          </div>
          <div className="dash-chart-card">
            <h4 className="dash-chart-title">Requests</h4>
            <div className="dash-chart-body">
              <MiniPie
                data={requestChartData}
                colors={[theme.occupied, theme.chart[4], theme.chart[5]]}
              />
            </div>
          </div>
        </div>

        {/* Accommodation — last, occasion filtered */}
        <div className="hotel-count-section dash-panel dash-accommodation">
          <div className="dash-accommodation-head">
            <h3 className="section-title mb-0 dash-accommodation-title">
              Accommodation
            </h3>

            {selectedOccasionId ? (
              <div className="hotel-summary-row">
                <div className="hotel-summary-chip">
                  <span className="hotel-summary-label">Hotels</span>
                  <strong>{accommodationData.summary?.hotels ?? 0}</strong>
                </div>
                <div className="hotel-summary-chip">
                  <span className="hotel-summary-label">Rooms</span>
                  <strong>{accommodationData.summary?.totalRooms ?? 0}</strong>
                </div>
                <div className="hotel-summary-chip is-occupied">
                  <span className="hotel-summary-label">Occupied</span>
                  <strong>{accommodationData.summary?.occupied ?? 0}</strong>
                </div>
                <div className="hotel-summary-chip is-available">
                  <span className="hotel-summary-label">Available</span>
                  <strong>{accommodationData.summary?.available ?? 0}</strong>
                </div>
                <div className="hotel-summary-chip">
                  <span className="hotel-summary-label">Bookings</span>
                  <strong>{accommodationData.summary?.bookings ?? 0}</strong>
                </div>
              </div>
            ) : (
              <div className="hotel-summary-row hotel-summary-row-empty" />
            )}

            {selectedOccasion ? (
              <span className="dash-selected-occasion">
                For: <strong>{selectedOccasion.OccasionName}</strong>
              </span>
            ) : (
              <span className="dash-selected-occasion">Select an occasion</span>
            )}
          </div>

          {!selectedOccasionId ? (
            <p className="hotel-empty mb-0">
              Select an occasion to view accommodation stats.
            </p>
          ) : (
            <>
              {!accommodationData.hotels?.length ? (
                <p className="hotel-empty mb-0">
                  No hotels added for this event.
                </p>
              ) : (
                <div className="dash-accommodation-charts">
                  <div className="dash-chart-card is-compact">
                    <h4 className="dash-chart-title">Room Occupancy</h4>
                    <div className="dash-chart-body">
                      <MiniPie
                        data={occupancyPieData}
                        colors={[theme.occupied, theme.available]}
                        showZero={false}
                      />
                    </div>
                  </div>
                  <div className="dash-chart-card is-wide">
                    <h4 className="dash-chart-title">Hotel-wise Rooms</h4>
                    <div className="dash-chart-body dash-bar-body">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={hotelBarData}
                          margin={{ top: 8, right: 12, left: -8, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2eded" />
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 11, fill: "#5b646c" }}
                          />
                          <YAxis
                            allowDecimals={false}
                            tick={{ fontSize: 11, fill: "#5b646c" }}
                          />
                          <Tooltip
                            content={<ChartTooltip />}
                            cursor={{ fill: "rgba(0, 0, 0, 0.04)" }}
                            {...smoothTooltipProps}
                          />
                          <Legend
                            iconType="circle"
                            wrapperStyle={{ fontSize: 11 }}
                          />
                          <Bar
                            dataKey="Occupied"
                            stackId="rooms"
                            fill={theme.occupied}
                            radius={[0, 0, 0, 0]}
                            isAnimationActive
                            animationDuration={650}
                            animationEasing="ease-out"
                          />
                          <Bar
                            dataKey="Available"
                            stackId="rooms"
                            fill={theme.available}
                            radius={[4, 4, 0, 0]}
                            isAnimationActive
                            animationDuration={650}
                            animationEasing="ease-out"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
